import { useState, useEffect, useCallback } from "react";
import { getCurrentUserId } from "../utils/currentUser";

const getColumnWidthsKey = (storageKey: string): string =>
  `ram_column_widths_${getCurrentUserId()}_${storageKey}`;

export type ColumnWidths = Record<string, number>;

interface UseColumnWidthsOptions {
  storageKey: string;
  defaultWidths: ColumnWidths;
}

export const useColumnWidths = ({
  storageKey,
  defaultWidths,
}: UseColumnWidthsOptions) => {
  const fullStorageKey = getColumnWidthsKey(storageKey);

  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    try {
      const stored = localStorage.getItem(fullStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new columns
        return { ...defaultWidths, ...parsed };
      }
    } catch (error) {
      console.error("Error loading column widths from localStorage:", error);
    }
    return defaultWidths;
  });

  // Save to localStorage whenever widths change
  useEffect(() => {
    try {
      localStorage.setItem(fullStorageKey, JSON.stringify(columnWidths));
    } catch (error) {
      console.error("Error saving column widths to localStorage:", error);
    }
  }, [columnWidths, fullStorageKey]);

  const updateColumnWidth = useCallback((columnKey: string, width: number) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: Math.max(width, 50), // Minimum width of 50px
    }));
  }, []);

  const resetColumnWidths = useCallback(() => {
    setColumnWidths(defaultWidths);
  }, [defaultWidths]);

  return {
    columnWidths,
    updateColumnWidth,
    resetColumnWidths,
    setColumnWidths,
  };
};

// Helper to extract default widths from column definitions
export const getDefaultWidthsFromColumns = (
  columns: Array<{
    key: string;
    width?: number;
    minWidth?: number;
    maxWidth?: number;
  }>,
): ColumnWidths => {
  const widths: ColumnWidths = {};
  columns.forEach((col) => {
    if (col.key && col.key !== "filler") {
      widths[col.key] = col.width ?? col.minWidth ?? col.maxWidth ?? 100;
    }
  });
  return widths;
};
